
import { createClient, SupabaseClient } from '@supabase/supabase-js';

export const getEnv = (key: string): string => {
  // 1. Vite / Modern Frontend
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    if (import.meta.env[key]) return import.meta.env[key];
    if (import.meta.env[`VITE_${key}`]) return import.meta.env[`VITE_${key}`];
  }
  // 2. Node.js (Testing/CLI)
  if (typeof process !== 'undefined' && process.env) {
    if (process.env[key]) return process.env[key];
    if (process.env[`VITE_${key}`]) return process.env[`VITE_${key}`];
  }
  // 3. Window / Global fallback
  if (typeof window !== 'undefined') {
    const win = window as any;
    if (win.process?.env?.[key]) return win.process.env[key];
    if (win[key]) return win[key];
  }
  return '';
};

const supabaseUrl = getEnv('SUPABASE_URL');
const supabaseAnonKey = getEnv('SUPABASE_ANON_KEY');

// ─── Supabase SupportedStorage interface ────────────────────────────────────
interface SupportedStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

// ─── Lazy Client with Secure Storage Support ────────────────────────────────
// The client is created lazily to support async secure storage initialization
// on native platforms (iOS Keychain / Android EncryptedSharedPreferences).

let _supabase: SupabaseClient | null = null;
let _initialized = false;

// Mock minimo per evitare crash se il client non è ancora inizializzato
const _mockClient = {
  auth: {
    getSession: async () => ({ data: { session: null }, error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => { } } } }),
    signOut: async () => ({ error: null }),
    setSession: async () => ({ data: { session: null, user: null }, error: null }),
    exchangeCodeForSession: async () => ({ data: { session: null, user: null }, error: null }),
  },
  from: () => ({
    select: () => ({ eq: () => ({ single: async () => ({ data: null, error: null }) }) }),
    update: () => ({ eq: async () => ({ data: null, error: null }) }),
    insert: () => ({ select: () => ({ single: async () => ({ data: null, error: null }) }) }),
    delete: () => ({ eq: async () => ({ data: null, error: null }) })
  })
} as unknown as SupabaseClient;

/**
 * Initialize the Supabase client with an optional custom storage adapter.
 * On native: pass the SecureStorageAdapter (Keychain / EncryptedSharedPreferences).
 * On web: call without arguments to use localStorage (default).
 *
 * MUST be called before any Supabase operation on native platforms.
 */
export function initSupabase(storageAdapter?: SupportedStorage): void {
  if (_initialized) return;

  if (!supabaseUrl || !supabaseAnonKey) {
    console.error("SUPABASE_URL o SUPABASE_ANON_KEY mancanti. L'app non potrà comunicare con il backend.");
    _supabase = _mockClient;
    _initialized = true;
    return;
  }

  // On native platforms (storageAdapter provided), disable detectSessionInUrl.
  // Native apps don't receive session tokens via URL — deep links are handled separately.
  // Leaving it enabled on Capacitor can cause the Supabase client to misparse the
  // WebView URL on cold start, potentially clearing a valid session from SecureStorage.
  const isNativeStorage = !!storageAdapter;

  _supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: !isNativeStorage,
      storage: storageAdapter || (typeof window !== 'undefined' ? window.localStorage : undefined),
    },
    global: {
      headers: {
        'x-client-info': 'supabase-js-web',
      },
    },
  });
  _initialized = true;
}

/**
 * Returns the initialized Supabase client.
 * Throws if called before initSupabase().
 */
export function getSupabase(): SupabaseClient {
  if (!_supabase) {
    // Auto-init with localStorage for backward compatibility (web, tests, SSR)
    initSupabase();
  }
  return _supabase!;
}

/**
 * Backward-compatible export: Proxy that delegates to the lazily-initialized client.
 * All existing `import { supabase } from '...'` continue to work without changes.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop: string | symbol) {
    const client = getSupabase();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
});
